import { useState } from 'react'
import MFAccordion from '../../../components/ui-components/MFAccordion'
import RenderHTML from '../../../components/ui-components/RenderHTML'
import { trackEvent } from '../../../utils/appBridge'
import { EventNames } from '../../../types/misc.types'
import { useNewEliteLandingAnalyticsScreen } from '../analytics/NewEliteLandingAnalyticsContext'
import { NewEliteLandingAnalyticsScreen } from '../analytics/newEliteLandingAnalytics'

import { useEliteBuyCtaExtras } from '../../../components/elite/EliteBuyCtaExtrasContext'
type NewEliteFaqItem = {
  title: string
  descp: string
}

const newEliteLandingFaqs: NewEliteFaqItem[] = [
  {
    title: 'What is the Elite membership and who is it for?',
    descp: `Elite is a yearly membership that gives access to high-quality advisory &amp; features and helps you manage your portfolio like the Top 1%.
    <p class="mt-3">Using data-driven insights and personalised recommendations, Elite helps you identify underperforming funds, stay invested in top performers, and build long-term wealth with greater confidence.</p>`
  },
  {
    title: 'What exactly do I get with Elite membership?',
    descp: `Elite gives you access to:
      <ul style="list-style-type: disc; padding-left: 20px;">
      <li><b>Power Rank</b> – Know the top-ranked funds in any category</li>
      <li><b>Elite Scores</b> – Evaluate a fund&apos;s true performance with important metrics like Consistency, Volatility, and Recency</li>
      <li><b>Rank Trends</b> – Track how a fund has ranked over the past 24 months</li>
      <li><b>Power Rebalancing</b> – A tax-efficient, personalised plan that helps you switch from underperforming funds to stronger ones - without changing your risk profile (reviewed monthly)</li>
      <li><b>Regular to Direct Plan</b> - A tax-efficient plan to help you switch to Direct funds</li>
      <li><b>Family Portfolios</b> - Manage all family investments in one consolidated view</li>
      <li><b>Tax-Saving Plan</b> - A personalised plan to help reduce your tax liability for the current FY and save on future taxes</li>
      </ul>`
  },
  {
    title: 'Does Elite guarantee higher returns?',
    descp: `No investment advisory service can guarantee that - and neither do we. What Elite offers is research-backed insights that helps you consistently make better decisions for your portfolio (choosing stronger funds, exiting underperforming funds, avoiding unnecessary churn).`
  },
  {
    title: 'Will Elite recommend new funds or only work with my existing funds?',
    descp: `Both. You can use Power Rank and Elite Scores to discover top-ranked funds before making an investment decision. Plus, Elite will continuously monitor your existing funds and recommend rebalancing if a significantly better alternative exists in the same category.`
  },
  {
    title: 'How often are the rankings and insights updated?',
    descp: 'All our recommendations and fund rankings are reviewed by experts and updated monthly.'
  },
  {
    title: 'Are the recommendations provided unbiased?',
    descp: 'Yes, PowerUp Money is a SEBI-registered RIA and 100% unbiased. We do not earn any commissions from mutual fund houses and our only revenue is the fixed Elite membership fee.'
  },
  {
    title: 'Can I pause or cancel my Elite membership?',
    descp: `Elite is a purchase that gives you access to a 1-month or 12-month membership, and cannot be paused or cancelled midway.
    <p class="mt-3">Throughout your membership, the fund rankings, portfolio insights, and personalised plans will continue to update monthly. Any new features added to Elite during this period will also be automatically included in your membership.</p>`
  },
  // {
  //   title: 'Will my Elite membership auto-renew?',
  //   descp: `No, Elite does not auto-renew. We&apos;ll notify you before your membership ends, and you can then choose whether you&apos;d like to renew it.`
  // },
  {
    title: 'Is my financial data secure?',
    descp:
      'Yes. We use industry-best encryption and follow strict data protection practices. Your personal and financial data is never shared with third parties, under any circumstance.'
  },
  {
    title: 'How do I cancel my UPI Autopay for Elite?',
    descp: `You can cancel it directly from the UPI app you used to set it up, such as Google Pay, PhonePe, Paytm, etc. Go to <strong>Autopay/ Mandates</strong>, find the mandate created for <strong>Uniorbit Wealth</strong>  and cancel it. Your Elite membership will remain active until the end of your current period.`
  }
]

const newEliteFamilyFaqs: NewEliteFaqItem[] = [
  {
    title: 'Who can I add as a family member?',
    descp:
      'You can add your spouse, parents, children, siblings, or even someone outside the family whose investments you manage.'
  },
  {
    title: 'Can I buy Elite for my family members?',
    descp:
      'Yes. As an Elite member, you can buy Elite for any of your family members at a special price of ₹749 for a yearly membership.'
  },
  {
    title: 'Will they get all the Elite benefits?',
    descp: `Yes, your family member will then receive all Elite benefits including:
      <ul style="list-style-type: disc; padding-left: 20px;">
      <li>Power Rank</li>
      <li>Elite Scores</li>
      <li>Rank Trends</li>
      <li>Power Rebalancing</li>
      <li>Regular to Direct Plan</li>
      <li>Tax-Saving Plan</li>
      </ul>`
  },
  {
    title: 'Will their Elite membership be affected if my membership expires?',
    descp:
      'No, their membership will not be affected. Once purchased, it runs independently and continues even if your own Elite expires.'
  }
]

export default function NewEliteLandingFAQs({
  faqHeader = 'FAQs',
  isFamilyMode = false
}: {
  faqHeader?: string
  isFamilyMode?: boolean
}) {
  const analyticsScreen = useNewEliteLandingAnalyticsScreen()
  const eliteBuyCtaExtras = useEliteBuyCtaExtras()
  const [accordionState, setAccordionState] = useState<Record<string, boolean>>({})

  let screenType = 'MF_New_Elite_Landing'
  if (
    analyticsScreen === NewEliteLandingAnalyticsScreen.Dashboard ||
    analyticsScreen === NewEliteLandingAnalyticsScreen.FamilyDashboard
  ) {
    screenType = 'MF_L2_New_Elite_Dashboard'
  } else if (analyticsScreen === NewEliteLandingAnalyticsScreen.FamilyLanding) {
    screenType = 'MF_New_Elite_Family_Landing'
  }

  const faqs = isFamilyMode ? newEliteFamilyFaqs : newEliteLandingFaqs

  return (
    <div className='bg-white px-5 pt-8 pb-8'>
      {faqHeader ? <div className='text-[#6A6E6E] text-[13px] pb-3'>{faqHeader}</div> : null}
      <div className='flex flex-col overflow-hidden rounded-xl border border-[#EBF2F2] divide-y divide-[#EBF2F2]'>
        {faqs.map(({ title, descp }, index) => (
          <div key={`${title}${index}`} className='bg-white'>
            <MFAccordion
              title={title}
              accordionHeaderClassName='!px-4 !min-h-12'
              accordionBodyClassName='!px-4 max-w-[90%]'
              isExpanded={Boolean(accordionState?.[title])}
              onAccordionClick={() => {
                setAccordionState((accordionStatePrev) => {
                  trackEvent({
                    eventName: EventNames.UserClicked,
                    payload: {
                      ...eliteBuyCtaExtras,
                      eventId: title,
                      responseName: accordionStatePrev?.[title] ? 'Collapse' : 'Expand',
                      ScreenName: 'FAQ',
                      screenType
                    }
                  })
                  return {
                    ...accordionStatePrev,
                    [title]: !accordionStatePrev?.[title]
                  }
                })
              }}
            >
              <div className='pb-4 text-[13px] leading-[150%] text-[#6A6E6E]'>
                <RenderHTML innerHTMLText={descp} />
              </div>
            </MFAccordion>
          </div>
        ))}
      </div>
    </div>
  )
}
